import { omit } from 'remeda';
import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityTwitter = z.object({
    type: z.literal(ELEMENT_TYPES.TWITTER),
    mimetype: z.literal(MIME_TYPES.TWITTER),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    originalSrc: z.string().nullable(),
    src: z.string(),
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullable(),
    }),
});
export type UnityTwitter = z.infer<typeof UnityTwitter>;

export const PlateTwitter = z.object({
    type: z.literal(ELEMENT_TYPES.TWITTER),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.TWITTER),
        templateElement: z.boolean().optional(),
        originalSrc: z.string().nullable(),
        src: z.string(),
        embed: z.object({
            caption: z.array(PlateParagraph),
            credit: z.string().nullable(),
        }),
    }),
    children: EmptyChildren,
});
export type TwitterElement = z.infer<typeof PlateTwitter>;

export const TwitterCodec = z.codec(UnityTwitter, PlateTwitter, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: value.mimetype,
            originalSrc: value.originalSrc,
            src: value.src,
            embed: {
                caption: value.contentAttributes.caption
                    ? value.contentAttributes.caption.richTextFormat.map((paragraph) =>
                          ParagraphCodec.decode(paragraph),
                      )
                    : [
                          {
                              type: ELEMENT_TYPES.PARAGRAPH,
                              id: crypto.randomUUID(),
                              children: [{ text: '' }],
                          },
                      ],
                credit: value.contentAttributes.credit ?? '',
            },
            ...(value.templateElement && { templateElement: value.templateElement }),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...omit(value.data, ['embed']),
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
        },
    }),
});
